import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';
import { useTreatmentPathwayOptions } from '../useTreatmentPathwayOptions';

/**
 * TreatmentPathwaysTriageFollowUpTab
 *
 * Handles Treatment Pathways section for Triage Follow Up reports
 * Maps to TriageInjuryDetails.Report, TriageDetails.Report, Risk.Triage, and Report.HealthcareDelivery tables
 *
 * Report types: 2003 (Triage Follow Up)
 * Section: psych-triage-followup-treatment-pathways
 * TabPerInjury: TRUE (per-injury tab)
 */

const TreatmentPathwaysTriageFollowUpTab = ({ reportId, api, onSave, isPublished, patientInfo }) => {
  // Form state
  const [treatmentPathwayChange, setTreatmentPathwayChange] = useState(null);
  const [treatmentPathwayID, setTreatmentPathwayID] = useState('');
  const [clinicalRationale, setClinicalRationale] = useState('');
  const [nextCallDue, setNextCallDue] = useState('');
  const [preferredTreatingClinicianGender, setPreferredTreatingClinicianGender] = useState('');
  const [noAssessments, setNoAssessments] = useState('');
  const [noTreatments, setNoTreatments] = useState('');
  const [summaryForAP, setSummaryForAP] = useState('');
  const [patientSummary, setPatientSummary] = useState('');
  const [closeCase, setCloseCase] = useState(null);
  const [disableReport, setDisableReport] = useState(null);
  const [dischargeProgress, setDischargeProgress] = useState('');
  const [patientPresentation, setPatientPresentation] = useState('');
  const [treatmentPlan, setTreatmentPlan] = useState('');
  const [completedTheropyToDate, setCompletedTheropyToDate] = useState('');
  const [outstandingComments, setOutstandingComments] = useState('');
  const [clientRiskSummary, setClientRiskSummary] = useState('');
  const [extensionRationale, setExtensionRationale] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [improvement, setImprovement] = useState('');
  const [signedBy, setSignedBy] = useState('');
  const [regNo, setRegNo] = useState('');
  const [signedDate, setSignedDate] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  // Dropdown options
  const { options: treatmentPathwayOptions } = useTreatmentPathwayOptions(
    api,
    reportId,
    patientInfo?.isCYPApplicable
  );
  const [clinicianGenderOptions] = useState([
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: 'A', label: 'Any' }
  ]);
  const [assessmentsOptions] = useState([
    { value: '0', label: '0' },
    { value: '1', label: '1' }
  ]);
  const [treatmentsOptions] = useState([
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' },
    { value: '13', label: '13' },
    { value: '14', label: '14' },
    { value: '15', label: '15' },
    { value: '16', label: '16' }
  ]);
  const [improvementOptions] = useState([
    { value: 'Fully Resolved', label: 'Fully Resolved' },
    { value: 'Resolving', label: 'Resolving' },
    { value: 'Function Maintained', label: 'Function Maintained' },
    { value: 'No Change', label: 'No Change' },
    { value: 'Worse', label: 'Worse' }
  ]);

  useEffect(() => {
    loadTreatmentPathwaysData();
  }, [reportId]);

  const dateOnlyToString = (dateOnly) => {
    if (!dateOnly) return '';
    // Handle ISO datetime string format "YYYY-MM-DDT00:00:00"
    if (typeof dateOnly === 'string') {
      return dateOnly.substring(0, 10);
    }
    // Handle DateOnly object {year, month, day}
    if (!dateOnly.year) return '';
    const year = dateOnly.year;
    const month = String(dateOnly.month).padStart(2, '0');
    const day = String(dateOnly.day).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const stringToDateOnly = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    return {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10),
      day: parseInt(parts[2], 10)
    };
  };

  const loadTreatmentPathwaysData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-followup-treatment-pathways`);

      if (response.ok) {
        const data = await response.json();

        setTreatmentPathwayChange(data.treatmentPathwayChange);
        setTreatmentPathwayID(data.treatmentPathwayID !== null ? data.treatmentPathwayID : '');
        setClinicalRationale(data.clinicalRationale || '');
        setNextCallDue(dateOnlyToString(data.nextCallDue));
        setPreferredTreatingClinicianGender(data.preferredTreatingClinicianGender || '');
        setNoAssessments(data.noAssessments !== null ? data.noAssessments : '');
        setNoTreatments(data.noTreatments !== null ? data.noTreatments : '');
        setSummaryForAP(data.summaryForAP || '');
        setPatientSummary(data.patientSummary || '');
        setCloseCase(data.closeCase);
        setDisableReport(data.disableReport);
        setDischargeProgress(data.dischargeProgress || '');
        setPatientPresentation(data.patientPresentation || '');
        setTreatmentPlan(data.treatmentPlan || '');
        setCompletedTheropyToDate(data.completedTheropyToDate || '');
        setOutstandingComments(data.outstandingComments || '');
        setClientRiskSummary(data.clientRiskSummary || '');
        setExtensionRationale(data.extensionRationale || '');
        setRecommendations(data.recommendations || '');
        setImprovement(data.improvement || '');
        setSignedBy(data.signedBy || '');
        setRegNo(data.regNo || '');
        setSignedDate(dateOnlyToString(data.signedDate));

        // All dropdown options (treatmentPathwayOptions, clinicianGenderOptions, assessmentsOptions, treatmentsOptions, and improvementOptions) are now hardcoded

        // Set loaded fields as 'saved'
        const initialSavingStates = {};
        if (data.treatmentPathwayChange !== null) initialSavingStates['TreatmentPathwayChange'] = 'saved';
        if (data.treatmentPathwayID !== null) initialSavingStates['TreatmentPathwayID'] = 'saved';
        if (data.clinicalRationale) initialSavingStates['ClinicalRationale'] = 'saved';
        if (data.nextCallDue) initialSavingStates['NextCallDue'] = 'saved';
        if (data.preferredTreatingClinicianGender) initialSavingStates['PreferredTreatingClinicianGender'] = 'saved';
        if (data.noAssessments !== null) initialSavingStates['NoAssessments'] = 'saved';
        if (data.noTreatments !== null) initialSavingStates['NoTreatments'] = 'saved';
        if (data.summaryForAP) initialSavingStates['SummaryForAP'] = 'saved';
        if (data.patientSummary) initialSavingStates['PatientSummary'] = 'saved';
        if (data.closeCase !== null) initialSavingStates['CloseCase'] = 'saved';
        if (data.disableReport !== null) initialSavingStates['DisableReport'] = 'saved';
        if (data.dischargeProgress) initialSavingStates['DischargeProgress'] = 'saved';
        if (data.patientPresentation) initialSavingStates['PatientPresentation'] = 'saved';
        if (data.treatmentPlan) initialSavingStates['TreatmentPlan'] = 'saved';
        if (data.completedTheropyToDate) initialSavingStates['CompletedTheropyToDate'] = 'saved';
        if (data.outstandingComments) initialSavingStates['OutstandingComments'] = 'saved';
        if (data.clientRiskSummary) initialSavingStates['ClientRiskSummary'] = 'saved';
        if (data.extensionRationale) initialSavingStates['ExtensionRationale'] = 'saved';
        if (data.recommendations) initialSavingStates['Recommendations'] = 'saved';
        if (data.improvement) initialSavingStates['Improvement'] = 'saved';
        if (data.signedDate) initialSavingStates['SignedDate'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading treatment pathways data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-followup-treatment-pathways/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({ value })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    const fieldValues = {
      'TreatmentPathwayChange': treatmentPathwayChange,
      'TreatmentPathwayID': treatmentPathwayID,
      'ClinicalRationale': clinicalRationale,
      'NextCallDue': nextCallDue,
      'PreferredTreatingClinicianGender': preferredTreatingClinicianGender,
      'NoAssessments': noAssessments,
      'NoTreatments': noTreatments,
      'SummaryForAP': summaryForAP,
      'PatientSummary': patientSummary,
      'CloseCase': closeCase,
      'DisableReport': disableReport,
      'DischargeProgress': dischargeProgress,
      'PatientPresentation': patientPresentation,
      'TreatmentPlan': treatmentPlan,
      'CompletedTheropyToDate': completedTheropyToDate,
      'OutstandingComments': outstandingComments,
      'ClientRiskSummary': clientRiskSummary,
      'ExtensionRationale': extensionRationale,
      'Recommendations': recommendations,
      'Improvement': improvement,
      'SignedDate': signedDate
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    saveField(fieldName, value);
  };

  const handleSelectChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleBooleanChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleDateChange = (fieldName, value, setter) => {
    setter(value);
    // API expects date-time string format "YYYY-MM-DDT00:00:00"
    const dateTimeValue = value ? `${value}T00:00:00` : null;
    saveField(fieldName, dateTimeValue);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading treatment pathways...</p>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-section">
        <h3>Treatment Pathways</h3>

        <div className="form-group">
          <label className="inline-label">
            <strong>Has the treatment pathway changed? <span style={{ color: 'red' }}>*</span></strong>
            {getSaveIndicator('TreatmentPathwayChange')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="treatmentPathwayChange"
                checked={treatmentPathwayChange === true}
                onChange={() => handleBooleanChange('TreatmentPathwayChange', true, setTreatmentPathwayChange)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="treatmentPathwayChange"
                checked={treatmentPathwayChange === false}
                onChange={() => handleBooleanChange('TreatmentPathwayChange', false, setTreatmentPathwayChange)}
              />
              No
            </label>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="treatmentPathwayID">
            Treatment Pathway: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('TreatmentPathwayID')}
          </label>
          <select
            id="treatmentPathwayID"
            value={treatmentPathwayID}
            onChange={(e) => handleSelectChange('TreatmentPathwayID', e.target.value, setTreatmentPathwayID)}
            style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
          >
            <option value="">Select</option>
            {treatmentPathwayOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="clinicalRationale">
            Clinical Rationale:
            {getSaveIndicator('ClinicalRationale')}
          </label>
          <textarea
            id="clinicalRationale"
            value={clinicalRationale}
            onChange={(e) => handleFieldChange('ClinicalRationale', e.target.value, setClinicalRationale)}
            onBlur={() => handleFieldBlur('ClinicalRationale', clinicalRationale)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="nextCallDue">
            When is the followup call due?
            {getSaveIndicator('NextCallDue')}
          </label>
          <input
            type="date"
            id="nextCallDue"
            value={nextCallDue}
            onChange={(e) => handleDateChange('NextCallDue', e.target.value, setNextCallDue)}
            style={{ width: '250px' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="preferredTreatingClinicianGender">
            Preferred treating clinician gender:
            {getSaveIndicator('PreferredTreatingClinicianGender')}
          </label>
          <select
            id="preferredTreatingClinicianGender"
            value={preferredTreatingClinicianGender}
            onChange={(e) => handleSelectChange('PreferredTreatingClinicianGender', e.target.value, setPreferredTreatingClinicianGender)}
            style={{ width: '100%', maxWidth: '300px', padding: '8px' }}
          >
            <option value="">Select</option>
            {clinicianGenderOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="noAssessments">
            Assessments:
            {getSaveIndicator('NoAssessments')}
          </label>
          <select
            id="noAssessments"
            value={noAssessments}
            onChange={(e) => handleSelectChange('NoAssessments', e.target.value, setNoAssessments)}
            style={{ width: '100%', maxWidth: '200px', padding: '8px' }}
          >
            <option value="">Select</option>
            {assessmentsOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="noTreatments">
            Treatments: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('NoTreatments')}
          </label>
          <select
            id="noTreatments"
            value={noTreatments}
            onChange={(e) => handleSelectChange('NoTreatments', e.target.value, setNoTreatments)}
            style={{ width: '100%', maxWidth: '200px', padding: '8px' }}
          >
            <option value="">Select</option>
            {treatmentsOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="dischargeProgress">
            Discharge progress:
            {getSaveIndicator('DischargeProgress')}
          </label>
          <textarea
            id="dischargeProgress"
            value={dischargeProgress}
            onChange={(e) => handleFieldChange('DischargeProgress', e.target.value, setDischargeProgress)}
            onBlur={() => handleFieldBlur('DischargeProgress', dischargeProgress)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="patientPresentation">
            Patient presentation:
            {getSaveIndicator('PatientPresentation')}
          </label>
          <textarea
            id="patientPresentation"
            value={patientPresentation}
            onChange={(e) => handleFieldChange('PatientPresentation', e.target.value, setPatientPresentation)}
            onBlur={() => handleFieldBlur('PatientPresentation', patientPresentation)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="treatmentPlan">
            Treatment plan:
            {getSaveIndicator('TreatmentPlan')}
          </label>
          <textarea
            id="treatmentPlan"
            value={treatmentPlan}
            onChange={(e) => handleFieldChange('TreatmentPlan', e.target.value, setTreatmentPlan)}
            onBlur={() => handleFieldBlur('TreatmentPlan', treatmentPlan)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="completedTheropyToDate">
            What has been completed with patient in therapy to date:
            {getSaveIndicator('CompletedTheropyToDate')}
          </label>
          <p className="field-help-text" style={{ margin: '0 0 8px 0', color: '#666', fontSize: '0.9em' }}>
            (list all components that have been completed, e.g. Cognitive restructuring, psychoeducation, formulation etc)
          </p>
          <textarea
            id="completedTheropyToDate"
            value={completedTheropyToDate}
            onChange={(e) => handleFieldChange('CompletedTheropyToDate', e.target.value, setCompletedTheropyToDate)}
            onBlur={() => handleFieldBlur('CompletedTheropyToDate', completedTheropyToDate)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="outstandingComments">
            What still needs to be covered and why:
            {getSaveIndicator('OutstandingComments')}
          </label>
          <textarea
            id="outstandingComments"
            value={outstandingComments}
            onChange={(e) => handleFieldChange('OutstandingComments', e.target.value, setOutstandingComments)}
            onBlur={() => handleFieldBlur('OutstandingComments', outstandingComments)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="clientRiskSummary">
            Risk assessment:
            {getSaveIndicator('ClientRiskSummary')}
          </label>
          <textarea
            id="clientRiskSummary"
            value={clientRiskSummary}
            onChange={(e) => handleFieldChange('ClientRiskSummary', e.target.value, setClientRiskSummary)}
            onBlur={() => handleFieldBlur('ClientRiskSummary', clientRiskSummary)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="extensionRationale">
            Rationale for requesting extension:
            {getSaveIndicator('ExtensionRationale')}
          </label>
          <textarea
            id="extensionRationale"
            value={extensionRationale}
            onChange={(e) => handleFieldChange('ExtensionRationale', e.target.value, setExtensionRationale)}
            onBlur={() => handleFieldBlur('ExtensionRationale', extensionRationale)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="recommendations">
            Comments and Recommendations:
            {getSaveIndicator('Recommendations')}
          </label>
          <textarea
            id="recommendations"
            value={recommendations}
            onChange={(e) => handleFieldChange('Recommendations', e.target.value, setRecommendations)}
            onBlur={() => handleFieldBlur('Recommendations', recommendations)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="improvement">
            Improvement:
            {getSaveIndicator('Improvement')}
          </label>
          <select
            id="improvement"
            value={improvement}
            onChange={(e) => handleSelectChange('Improvement', e.target.value, setImprovement)}
            style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
          >
            <option value="">Select</option>
            {improvementOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="summaryForAP">
            Notes for clinic:
            {getSaveIndicator('SummaryForAP')}
          </label>
          <textarea
            id="summaryForAP"
            value={summaryForAP}
            onChange={(e) => handleFieldChange('SummaryForAP', e.target.value, setSummaryForAP)}
            onBlur={() => handleFieldBlur('SummaryForAP', summaryForAP)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="patientSummary">
            Patient Summary:
            {getSaveIndicator('PatientSummary')}
          </label>
          <textarea
            id="patientSummary"
            value={patientSummary}
            onChange={(e) => handleFieldChange('PatientSummary', e.target.value, setPatientSummary)}
            onBlur={() => handleFieldBlur('PatientSummary', patientSummary)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label className="inline-label">
            <strong>Do you want to close the case? <span style={{ color: 'red' }}>*</span></strong>
            {getSaveIndicator('CloseCase')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="closeCase"
                checked={closeCase === true}
                onChange={() => handleBooleanChange('CloseCase', true, setCloseCase)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="closeCase"
                checked={closeCase === false}
                onChange={() => handleBooleanChange('CloseCase', false, setCloseCase)}
              />
              No
            </label>
          </div>
        </div>

        <div className="form-group">
          <label className="inline-label">
            <strong>Disable Report: <span style={{ color: 'red' }}>*</span></strong>
            {getSaveIndicator('DisableReport')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="disableReport"
                checked={disableReport === true}
                onChange={() => handleBooleanChange('DisableReport', true, setDisableReport)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="disableReport"
                checked={disableReport === false}
                onChange={() => handleBooleanChange('DisableReport', false, setDisableReport)}
              />
              No
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>
            Virtual assessment completed by:
          </label>
          <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            {signedBy || 'Not set'}
          </div>
        </div>

        <div className="form-group">
          <label>
            Registration number of clinician:
          </label>
          <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            {regNo || 'Not set'}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="signedDate">
            Triage call date: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('SignedDate')}
          </label>
          <input
            type="date"
            id="signedDate"
            value={signedDate}
            onChange={(e) => handleDateChange('SignedDate', e.target.value, setSignedDate)}
            style={{ width: '250px' }}
          />
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default TreatmentPathwaysTriageFollowUpTab;
